import { US_COUNTRY_MAPPER } from '@/modules/ta/widget/mapbox.constants';
import { isFinite, map } from 'lodash';

export class MapboxAutoZoomService {
  constructor(topPercent = 100) {
    this.topPercent = topPercent; // take top X% points by field
  }

  // Parse raw data into {lon, lat, field}
  parseData(rawData) {
    return rawData
      .filter(
        (d) =>
          Array.isArray(d.coordinates) &&
          !isNaN(parseFloat(d.coordinates[0])) &&
          !isNaN(parseFloat(d.coordinates[1])) &&
          isFinite(parseFloat(d.coordinates[0])) &&
          isFinite(parseFloat(d.coordinates[1]))
      )
      .map((d) => ({
        lon: parseFloat(d.coordinates[0]),
        lat: parseFloat(d.coordinates[1]),
        field: parseInt(String(d.field).replace(/,/g, '')),
      }));
  }

  // Get top X% of data points based on field value
  getTopPercent(parsedData) {
    // Sort descending by field
    let sorted = [...parsedData].sort((a, b) => b.field - a.field);
    // Compute how many to take
    let count = Math.ceil((this.topPercent / 100) * sorted.length);
    return sorted.slice(0, count);
  }

  // Compute bounds from points
  computeBounds(points) {
    let lons = points.map((d) => d.lon);
    let lats = points.map((d) => d.lat);

    return [
      [Math.min(...lons), Math.min(...lats)], // southwest
      [Math.max(...lons), Math.max(...lats)], // northeast
    ];
  }

  // Main method: auto-zoom based on top % values
  getBounds(rawData) {
    if (rawData.length === 0) return [];

    let parsedData = this.parseData(rawData);
    let topPoints = this.getTopPercent(parsedData);
    return topPoints.length > 0 ? this.computeBounds(topPoints) : [];
  }
}

export class MapboxLocationMapper {
  static normalizeState(location) {
    if (!location) return null;

    const key = location.trim().toUpperCase();

    if (US_COUNTRY_MAPPER.STATE_3CODE_TO_ISO[key]) return US_COUNTRY_MAPPER.STATE_3CODE_TO_ISO[key];

    // If already 2-letter ISO, return as uppercase
    if (/^[A-Z]{2}$/.test(key)) return key;

    return location; // Unknown input
  }
}

export class MapboxFeatureBoundsHandler {
  static getBound(features) {
    let minLat = Infinity,
      minLng = Infinity;
    let maxLat = -Infinity,
      maxLng = -Infinity;

    features.forEach((feature) => {
      const coordsArray =
        feature.geometry.type === 'Polygon'
          ? [feature.geometry.coordinates]
          : feature.geometry.coordinates; // MultiPolygon

      coordsArray.forEach((polygon) => {
        polygon.forEach((ring) => {
          ring.forEach(([lng, lat]) => {
            minLat = Math.min(minLat, lat);
            minLng = Math.min(minLng, lng);
            maxLat = Math.max(maxLat, lat);
            maxLng = Math.max(maxLng, lng);
          });
        });
      });
    });

    return [
      [minLng, minLat], // SW
      [maxLng, maxLat], // NE
    ];
  }

  static getAllCoordinates(geometry) {
    if (geometry.type === 'Point') return [geometry.coordinates];
    if (geometry.type === 'LineString') return geometry.coordinates;
    if (geometry.type === 'Polygon') return geometry.coordinates.flat(1);
    if (geometry.type === 'MultiPolygon') return geometry.coordinates.flat(2);
    if (geometry.type === 'MultiLineString') return geometry.coordinates.flat(1);
    return [];
  }
}

export class MapboxDataColumnsService {
  // Extract selected columns with details from widget metadata
  static getFirstSelectedColumn(widget) {
    const columns = map(widget.metadata.data_columns.selected, (column) => ({
      field: column.field,
    }));
    if (columns.length !== 0) {
      return columns[0];
    }
    return null;
  }

  static getGroupedColumn(widget) {
    const columns = map(widget.metadata.data_columns.grouped, (column) => ({
      field: column.groupby_name_field,
    }));
    if (columns.length !== 0) {
      return columns[0];
    }
    return null;
  }
}

export class MapboxDataService {
  static sortData(data, column) {
    return [...data].sort((a, b) => a[column] - b[column]);
  }

  static getTopPercent(data, column, topPercent) {
    const sortedData = this.sortData(data, column);
    const topCount = Math.ceil((topPercent / 100) * sortedData.length);
    return sortedData.slice(0, topCount);
  }
}
